/* =========================================================================
   Settings → Bunny CDN / Email (SMTP) / Email routing / Stripe.

   Backed by /platforms/credentials/:platform (the same store as the Google
   Play / Apple credentials): saved values are encrypted server-side and win
   over .env; for Bunny / Stripe a blank field falls back to .env (SMTP and
   routing are form-only). Secrets are write-only -- the
   API only says whether one is set and where from.

   RBAC: settings.view to see, settings.edit to save or test (the backend
   enforces both).
   ========================================================================= */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./Settings.module.css";
import { MdFileUpload } from "react-icons/md";
import {
  getIntegrationCredentials,
  testIntegrationCredentials,
  updateIntegrationCredentials,
  UploadImage,
} from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";
import useUnsavedChangesGuard from "./useUnsavedChangesGuard";
import { integrationFormFrom, integrationPatch } from "./integrationFields";

const formatWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

const firstMessage = (value) => (Array.isArray(value) ? value[0] : value);

// Email clients don't render ICO/SVG reliably, so the email logo takes raster images only.
const UPLOAD_ACCEPT = "image/png,image/jpeg,image/webp,image/gif";
const UPLOAD_MAX_BYTES = 2 * 1024 * 1024;

const IntegrationSettings = ({ section, hidden = false, onDirtyChange }) => {
  const { can } = useRbac();
  const canEdit = can("settings", "edit");
  const canUpload = canEdit && can("media", "create");
  const [uploadingField, setUploadingField] = useState(null);

  const [masked, setMasked] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [values, setValues] = useState(() => integrationFormFrom(section, null));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testTemplate, setTestTemplate] = useState(() => section.sendTest?.templates?.[0]?.key || "");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const applyMasked = useCallback(
    (data) => {
      setMasked(data);
      setValues(integrationFormFrom(section, data));
      setErrors({});
    },
    [section]
  );

  useEffect(() => {
    let cancelled = false;
    getIntegrationCredentials(section.key)
      .then((res) => !cancelled && applyMasked(res?.data ?? null))
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || `Could not load ${section.title} settings.`);
      });
    return () => {
      cancelled = true;
    };
  }, [section, applyMasked]);

  const patch = useMemo(() => integrationPatch(section, values, masked), [section, values, masked]);
  const dirty = canEdit && masked !== null && Object.keys(patch).length > 0;
  useUnsavedChangesGuard(dirty);
  useEffect(() => {
    onDirtyChange?.(section.key, dirty);
  }, [dirty, section.key, onDirtyChange]);

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateIntegrationCredentials(section.key, patch);
      applyMasked(res?.data ?? null);
      setTestResult(null);
      showToast(`${section.title} saved.`, "success");
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 422 && data?.errors) {
        setErrors(Object.fromEntries(Object.entries(data.errors).map(([k, v]) => [k, firstMessage(v)])));
        showToast(data.message || "Some fields are invalid.", "error");
      } else {
        showToast(data?.message || `Could not save ${section.title}.`, "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testIntegrationCredentials(section.key);
      setTestResult({ ok: true, message: res?.message || "Connection OK" });
    } catch (err) {
      const data = err?.response?.data;
      setTestResult({
        ok: false,
        message: data?.data?.error || data?.message || "Connection test failed",
        hint: data?.data?.hint,
      });
    } finally {
      setTesting(false);
      // The backend records the outcome; reload so "last checked" is current.
      getIntegrationCredentials(section.key)
        .then((res) => setMasked(res?.data ?? null))
        .catch(() => {});
    }
  };

  // Email routing: one test mail from each saved department address to testTo.
  const handleSendTest = async () => {
    setSendingTest(true);
    setSendResult(null);
    try {
      const res = await testIntegrationCredentials(section.key, {
        to: testTo.trim(),
        ...(testTemplate ? { template: testTemplate } : {}),
      });
      setSendResult({ ok: true, message: res?.message, results: res?.data?.results || [] });
    } catch (err) {
      const data = err?.response?.data;
      setSendResult({
        ok: false,
        message: firstMessage(data?.errors?.to) || data?.message || "Could not send test emails",
        results: data?.data?.results || [],
      });
    } finally {
      setSendingTest(false);
    }
  };

  // Image fields (the email logo): upload to the tenant's Bunny CDN, put the URL
  // in the field; Save persists it. "branding" uploads aren't cropped.
  const handleUpload = async (field, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!UPLOAD_ACCEPT.split(",").includes(file.type)) {
      showToast("Please choose a PNG, JPG, WebP or GIF image.", "error");
      return;
    }
    if (file.size > UPLOAD_MAX_BYTES) {
      showToast("Image must be 2 MB or smaller.", "error");
      return;
    }
    setUploadingField(field.name);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("folder", "branding");
      const res = await UploadImage(fd);
      const url = res?.data?.data?.upload?.file_url || res?.data?.data?.url || res?.data?.url || "";
      if (!url) {
        showToast("Upload finished but no image URL came back.", "error");
        return;
      }
      handleChange(field.name, url);
      showToast(`${field.label} uploaded — click Save changes to apply it.`, "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Image upload failed.", "error");
    } finally {
      setUploadingField(null);
    }
  };

  const handleDiscard = () => {
    setValues(integrationFormFrom(section, masked));
    setErrors({});
  };

  // Small badge next to each label: where the value in effect comes from.
  const badge = (field) => {
    const info = masked?.fields?.[field.name];
    if (!info?.configured) return { text: "Not set", tone: styles.badgeMuted };
    if (info.source === "env") return { text: ".env", tone: styles.badgeEnv, title: "From the server's .env file. Save a value here to override it." };
    if (info.source === "superadmin") return { text: "Platform", tone: styles.badgeEnv, title: "Set up for you by the platform. Saving here makes it your own copy." };
    return { text: "Saved", tone: styles.badgeSaved };
  };

  const secretHint = (field) => {
    const info = masked?.fields?.[field.name];
    if (!info?.configured) return "";
    if (info.source === "env") return "Type a new value to override .env";
    if (info.source === "superadmin") return "Set by the platform · leave blank to keep it";
    return `Leave blank to keep the saved value${info.fingerprint ? ` · ref ${info.fingerprint}` : ""}`;
  };

  const renderField = (field) => {
    const id = `integration-${section.key}-${field.name}`;
    const error = errors[field.name];
    const b = badge(field);
    const help = [field.secret ? secretHint(field) : "", field.help].filter(Boolean).join(" · ");
    return (
      <div key={field.name} className={`${styles.intField} ${field.wide ? styles.intFieldWide : ""}`}>
        <div className={styles.intLabelRow}>
          <label htmlFor={id} className={styles.intLabel}>
            {field.label}
          </label>
          <span className={`${styles.badge} ${b.tone}`} title={b.title}>
            {b.text}
          </span>
        </div>
        <div className={styles.inputLine}>
          <input
            id={id}
            name={field.name}
            type={field.secret ? "password" : "text"}
            autoComplete={field.secret ? "new-password" : "off"}
            spellCheck={false}
            value={values[field.name] ?? ""}
            placeholder={field.secret ? (masked?.fields?.[field.name]?.configured ? "••••••••••••" : "Not set") : field.placeholder}
            onChange={(e) => handleChange(field.name, e.target.value)}
            readOnly={!canEdit}
            disabled={!canEdit}
            aria-invalid={Boolean(error)}
            aria-describedby={`${id}-meta`}
            className={`${styles.intInput} ${error ? styles.inputError : ""}`}
          />
          {field.upload && canUpload && (
            <label className={`${styles.uploadBtn} ${uploadingField ? styles.uploadBtnBusy : ""}`}>
              <input
                type="file"
                accept={UPLOAD_ACCEPT}
                className={styles.visuallyHidden}
                disabled={Boolean(uploadingField)}
                onChange={(e) => handleUpload(field, e)}
                aria-label={`Upload ${field.label.toLowerCase()}`}
              />
              <MdFileUpload aria-hidden="true" />
              {uploadingField === field.name ? "Uploading…" : "Upload"}
            </label>
          )}
        </div>
        {field.upload && /^https?:\/\//i.test(String(values[field.name] || "").trim()) && (
          <img src={String(values[field.name]).trim()} alt={`${field.label} preview`} className={styles.intLogoPreview} />
        )}
        <div id={`${id}-meta`} className={styles.intMeta}>
          {error ? (
            <span role="alert" className={styles.errorText}>{error}</span>
          ) : (
            help && <span className={styles.fieldHelp}>{help}</span>
          )}
        </div>
      </div>
    );
  };

  // Connection pill: latest test this session, else the last recorded one.
  const connection = testResult
    ? testResult.ok
      ? { tone: styles.pillOk, text: "Connected" }
      : { tone: styles.pillFail, text: "Connection failed" }
    : masked?.last_verified_at
      ? masked.last_verification_ok
        ? { tone: styles.pillOk, text: "Connected", when: masked.last_verified_at }
        : { tone: styles.pillFail, text: "Last test failed", when: masked.last_verified_at }
      : { tone: styles.pillIdle, text: "Not tested" };

  return (
    <section className={styles.intPanel} hidden={hidden} aria-label={section.title}>
      <header className={styles.intHeader}>
        <div>
          <h3 className={styles.sectionHeading}>{section.title}</h3>
          <p className={styles.sectionIntro}>{section.intro}</p>
        </div>
        {masked && (
          <div className={styles.intHeaderActions}>
            {masked.summary?.mode && (
              <span
                className={`${styles.pill} ${masked.summary.mode === "live" ? styles.pillLive : styles.pillTest}`}
                title={masked.summary.mode === "live" ? "Real payments" : "Stripe test mode -- no real money moves"}
              >
                {masked.summary.mode === "live" ? "LIVE" : "TEST"} mode
              </span>
            )}
            {section.testable !== false && (
              <span className={`${styles.pill} ${connection.tone}`} title={connection.when ? `Checked ${formatWhen(connection.when)}` : undefined}>
                <span className={styles.pillDot} aria-hidden="true" />
                {connection.text}
              </span>
            )}
            {canEdit && section.testable !== false && (
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleTest}
                disabled={testing || dirty}
                title={dirty ? "Save first -- the test uses the saved settings" : undefined}
              >
                {testing ? "Testing…" : "Test connection"}
              </button>
            )}
          </div>
        )}
      </header>

      {!canEdit && <p className={styles.readOnlyNote}>You have view-only access to Settings.</p>}

      {testResult && (
        <div className={`${styles.testResult} ${testResult.ok ? styles.testOk : styles.testFail}`} role="status">
          <strong>{testResult.ok ? "✓ " : "✗ "}{testResult.message}</strong>
          {testResult.hint && <div>{testResult.hint}</div>}
        </div>
      )}

      {loadError ? (
        <p role="alert" className={styles.errorText}>{loadError}</p>
      ) : masked === null ? (
        <p className={styles.sectionIntro}>Loading…</p>
      ) : (
        <>
          {section.groups.map((group) => (
            <fieldset key={group.key} className={styles.intGroup}>
              <legend className={styles.intGroupTitle}>
                {group.title}
                {group.description && <span className={styles.intGroupDesc}>{group.description}</span>}
              </legend>
              <div className={styles.intGrid}>
                {section.fields.filter((f) => f.group === group.key).map(renderField)}
              </div>
            </fieldset>
          ))}

          {canEdit && section.sendTest && (
            <fieldset className={styles.intGroup}>
              <legend className={styles.intGroupTitle}>
                {section.sendTest.title}
                <span className={styles.intGroupDesc}>{section.sendTest.description}</span>
              </legend>
              <div className={styles.intGrid}>
                <div className={`${styles.intField} ${styles.intFieldWide}`}>
                  <div className={styles.intLabelRow}>
                    <label htmlFor={`integration-${section.key}-testTo`} className={styles.intLabel}>
                      Send to
                    </label>
                  </div>
                  <div className={styles.intSendRow}>
                    {section.sendTest.templates && (
                      <select
                        aria-label="Which email to send"
                        value={testTemplate}
                        onChange={(e) => setTestTemplate(e.target.value)}
                        className={`${styles.intInput} ${styles.intTemplateSelect}`}
                      >
                        {section.sendTest.templates.map((t) => (
                          <option key={t.key} value={t.key}>{t.label}</option>
                        ))}
                      </select>
                    )}
                    <input
                      id={`integration-${section.key}-testTo`}
                      type="email"
                      autoComplete="off"
                      spellCheck={false}
                      value={testTo}
                      placeholder="you@example.com"
                      onChange={(e) => setTestTo(e.target.value)}
                      className={styles.intInput}
                    />
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={handleSendTest}
                      disabled={sendingTest || dirty || !testTo.trim()}
                      title={dirty ? "Save first -- the test uses the saved settings" : undefined}
                    >
                      {sendingTest ? "Sending…" : section.sendTest.button}
                    </button>
                  </div>
                  <div className={styles.intMeta}>
                    <span className={styles.fieldHelp}>
                      {dirty ? "Save your changes first — the test uses the saved settings." : section.sendTest.help}
                    </span>
                  </div>
                </div>
              </div>
              {sendResult && (
                <div className={`${styles.testResult} ${sendResult.ok ? styles.testOk : styles.testFail}`} role="status">
                  <strong>{sendResult.ok ? "✓ " : "✗ "}{sendResult.message}</strong>
                  {sendResult.results.map((r) => (
                    <div key={r.field}>
                      {r.label}:{" "}
                      {r.status === "sent"
                        ? `sent for ${r.address}`
                        : r.status === "skipped"
                          ? "not set, skipped"
                          : `failed for ${r.address}${r.error ? ` -- ${r.error}` : ""}`}
                    </div>
                  ))}
                </div>
              )}
            </fieldset>
          )}

          {canEdit && (
            <div className={`${styles.intFooter} ${dirty ? styles.intFooterDirty : ""}`}>
              <span className={styles.fieldHelp}>
                {dirty ? "You have unsaved changes" : "Saved values apply right away; other servers pick them up within a minute."}
              </span>
              <div className={styles.intFooterActions}>
                {dirty && (
                  <button type="button" className={styles.ghostBtn} onClick={handleDiscard} disabled={saving}>
                    Discard
                  </button>
                )}
                <button type="button" className={styles.primaryBtn} onClick={handleSave} disabled={saving || !dirty}>
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default IntegrationSettings;
